import { Landmark } from 'lucide-react';
import { Building2, GraduationCap, MapPin, AlertTriangle, CheckCircle2, BarChart3, Activity, Clock } from 'lucide-react';
import { useDashboardApi, KPICard, LoadingSpinner, ErrorPanel, EntityStatusList, AlertsList, FeedTable, StatsPanel, ResponsiveStyles } from '../../components/dashboard/DashboardShared';
import { useAuthStore } from '../../store/authStore';
import '../../components/ui/datatable.css';

export function DashboardEstado() {
  // Pegar estadoId do usuario logado (JWT carrega este campo)
  const user = useAuthStore((s) => s.user);
  const estadoId = user?.estadoId;

  const { data, isLoading, error } = useDashboardApi(
    `dashboard-estado-${estadoId}`,
    estadoId ? `/estados/${estadoId}/dashboard` : '',
  );

  if (!estadoId) return <LoadingSpinner label="Identificando estado..." />;
  if (isLoading) return <LoadingSpinner label="Carregando Dashboard Estadual..." />;
  if (error) return <ErrorPanel />;

  const c = data?.cards || {};
  const chamadas = data?.operacao?.resumoChamadas || {};
  const notif = data?.analitico?.resumoNotificacoes || {};

  return (
    <div className="admin-page animate-fadeIn">
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 12px', borderRadius: 99, fontSize: '.75rem', fontWeight: 700,
              background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
            }}>
              <Landmark size={12} /> REDE VINCULADA: {data?.estado?.rede?.nome || 'REDE ESTADUAL'}
            </span>
            <span style={{ fontSize: '.75rem', color: '#94a3b8' }}>•</span>
            <span style={{ fontSize: '.75rem', fontWeight: 600, color: '#64748b' }}>
              Jurisdição: {data?.estado?.nome} ({data?.estado?.sigla})
            </span>
          </div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
            <Landmark size={28} style={{ color: '#2563eb' }} />
            {data?.estado?.nome || 'Dashboard Estadual'} ({data?.estado?.sigla})
          </h1>
          <p style={{ marginTop: 4 }}>Visão consolidada das escolas sob gestão estadual — {new Date().toLocaleDateString('pt-BR')}</p>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-4">
        <KPICard icon={MapPin} label="Municípios" value={c.totalMunicipios || 0} sub={`${c.totalMunicipiosComProblema || 0} com alertas`} color="#2563eb" />
        <KPICard icon={Building2} label="Escolas" value={c.totalEscolas || 0} color="#059669" />
        <KPICard icon={GraduationCap} label="Alunos" value={c.totalAlunosPrevistos?.toLocaleString('pt-BR') || 0} color="#6366f1" />
        <KPICard icon={CheckCircle2} label="Presentes" value={c.totalPresentes?.toLocaleString('pt-BR') || 0} sub={`${c.totalAtrasados || 0} atrasados`} color="#16a34a" />
      </div>

      <div className="kpi-grid kpi-grid-4" style={{ marginTop: 16 }}>
        <KPICard icon={AlertTriangle} label="Ausentes" value={c.totalAusentes?.toLocaleString('pt-BR') || 0} color="#dc2626" />
        <KPICard icon={AlertTriangle} label="Inconsistências" value={c.totalInconsistencias || 0} color="#d97706" />
        <KPICard icon={AlertTriangle} label="Alertas Ativos" value={c.totalAlertasAtivos || 0} color="#ef4444" />
        <KPICard icon={AlertTriangle} label="Saíram" value={c.totalSaiu?.toLocaleString('pt-BR') || 0} color="#6b7280" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 24 }}>
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChart3 size={18} style={{ color: '#2563eb' }} /> Status por Município
          </h3>
          <EntityStatusList items={data?.operacao?.statusMunicipios || []} nameKey="municipio" subLabel="escolas" />
        </div>

        <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} style={{ color: '#dc2626' }} /> Alertas Críticos
          </h3>
          <AlertsList alertas={data?.alertas?.criticos || []} />
        </div>
      </div>

      {/* Ranking de Risco */}
      {(data?.analitico?.rankingRisco || []).length > 0 && (
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20, marginTop: 24 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} style={{ color: '#d97706' }} /> Ranking de Risco por Município
          </h3>
          <div style={{ maxHeight: 250, overflowY: 'auto' }}>
            <table className="dt-table" style={{ width: '100%', fontSize: '0.82rem' }}>
              <thead><tr><th>Município</th><th>Alertas</th><th>Disp. Offline</th><th>Inconsistências</th><th>Índice</th></tr></thead>
              <tbody>
                {(data.analitico.rankingRisco || []).slice(0, 10).map((r: any) => (
                  <tr key={r.municipio.id}>
                    <td style={{ fontWeight: 600 }}>{r.municipio.nome}</td>
                    <td style={{ color: r.totalAlertasAtivos > 0 ? '#dc2626' : undefined }}>{r.totalAlertasAtivos}</td>
                    <td style={{ color: r.totalDispositivosProblema > 0 ? '#d97706' : undefined }}>{r.totalDispositivosProblema}</td>
                    <td>{r.totalInconsistenciasAbertas}</td>
                    <td><strong style={{ color: r.indiceRisco > 20 ? '#dc2626' : r.indiceRisco > 5 ? '#d97706' : '#16a34a' }}>{r.indiceRisco}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 24 }}>
        <StatsPanel title="Notificações Hoje" icon={Clock} iconColor="#6366f1" items={[
          { label: 'Total', value: notif.total || 0 },
          { label: 'Lidas', value: notif.lidas || 0, color: '#16a34a' },
          { label: 'Falhas', value: notif.falhas || 0, color: '#dc2626' },
          { label: 'Taxa Leitura', value: `${notif.taxaLeitura || 0}%`, color: '#7c3aed' },
        ]} />
        <StatsPanel title="Chamadas Hoje" icon={CheckCircle2} iconColor="#059669" items={[
          { label: 'Total', value: chamadas.total || 0 },
          { label: 'Abertas', value: chamadas.abertas || 0, color: '#d97706' },
          { label: 'Concluídas', value: chamadas.concluidas || 0, color: '#16a34a' },
          { label: 'Canceladas', value: chamadas.canceladas || 0, color: '#6b7280' },
        ]} />
      </div>

      <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20, marginTop: 24 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={18} style={{ color: '#2563eb' }} /> Feed — Últimos Eventos
        </h3>
        <FeedTable feed={data?.operacao?.feed || []} />
      </div>

      <ResponsiveStyles />
    </div>
  );
}
